import Section from "components/common/Section";
import StaticPageHeader from "components/common/StaticPageHeader";
import usePageMeta from "hooks/usePageMeta";

const About = () => {
  usePageMeta(
    "About Ubay | Transform the world through revealed knowledge",
    "Learn about Ubay.pk – a purpose-driven, non-profit Islamic platform founded in 2026 to connect people to the Quran through daily questions. Operating in Pakistan.",
    "/about"
  );

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      <img src="/assets/imgs/bg.jpg" alt="" className="fixed inset-0 h-full w-full object-cover -z-10" />

      <StaticPageHeader currentPage="About" />
      <Section asMain>
        <div className="py-8 sm:py-12">
          <div className="max-w-5xl mx-auto space-y-8">

            {/* Hero */}
            <div className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl p-6 sm:p-8 shadow-xl">
              <p className="text-sm font-semibold text-emerald-600 mb-2">Trying to transform the world through revealed knowledge</p>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3">About Ubay</h1>
              <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
                Ubay.pk is a purpose-driven, non-profit Islamic platform. We post one thoughtful question every day, reveal the correct answer the next day, and celebrate participants who learn consistently. Our goal is to connect people to the Quran in a clear, approachable, and respectful way.
              </p>
            </div>

            {/* Two-column grid */}
            <div className="grid gap-5 sm:gap-6 md:grid-cols-2">
              <div className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl p-6 shadow-lg space-y-3">
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Who we are</h2>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  Founded in 2026, Ubay.pk exists to make Quranic guidance accessible, accurate, and engaging. We are a non-profit with no commercial interests, focused solely on serving readers with honesty and responsibility.
                </p>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  Our team curates concise daily questions that can be answered in about a minute, keeping you moving toward understanding with consistency.
                </p>
              </div>

              <div className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl p-6 shadow-lg space-y-3">
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Our mission</h2>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  Remove barriers to the Quran, foster daily reflection, and help individuals grow spiritually. We aim to be a trusted companion for anyone seeking to engage with the Quran as part of everyday life.
                </p>
              </div>
            </div>

            {/* Three-column grid */}
            <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl p-5 sm:p-6 shadow-lg space-y-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center mb-2">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900">Key features</h3>
                <ul className="list-disc list-inside text-slate-700 text-sm sm:text-base space-y-1.5">
                  <li>One daily question; answer revealed the next day.</li>
                  <li>Lightweight experience — about 1 minute a day.</li>
                  <li>User-friendly on any device.</li>
                  <li>Free to use; non-commercial.</li>
                </ul>
              </div>

              <div className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl p-5 sm:p-6 shadow-lg space-y-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center mb-2">
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900">Research & curation</h3>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  Our Chief Content Officer, Mrs. Nousheen Sadaf Hashmi, oversees research and content integrity. She ensures accuracy, gravity, and clarity so Quranic teachings resonate with a truth-seeking audience.
                </p>
              </div>

              <div className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl p-5 sm:p-6 shadow-lg space-y-3 sm:col-span-2 lg:col-span-1">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center mb-2">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900">Why it matters</h3>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  Every question is designed to spark curiosity, deepen understanding, and build a habit of daily reflection. Small, consistent steps lead to lasting spiritual growth.
                </p>
              </div>
            </div>

            {/* Operating note */}
            <div className="bg-emerald-50/90 backdrop-blur-xl border border-emerald-200/60 rounded-2xl p-5 sm:p-6 shadow-lg">
              <p className="text-emerald-900 font-semibold text-base">Operating note</p>
              <p className="text-emerald-800 text-sm sm:text-base">Currently we are only operating in Pakistan.</p>
            </div>

          </div>
        </div>
      </Section>
    </div>
  );
};

export default About;
